import { useRef, useState } from "react";
import { Button, Flex, Modal } from "antd";
import {
  DownloadOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import html2canvas from "html2canvas";

import { IMAGE_BASE_URL } from "../../../constants/api";
import { HOTEL_ID_SERIES } from "../../../constants/constent";
import LS_SERVICE from "../../../utils/localStorage";

/* Resolve the tenant's primary color to a concrete hex (html2canvas cannot
   read CSS variables, so we need a real color for the exported PNG). */
const resolvePrimaryHex = () => {
  const { theme } = LS_SERVICE.get("setting") || {};
  if (theme) return "#" + String(theme).replace(/^#/, "");
  if (typeof window !== "undefined") {
    const cssVar = getComputedStyle(document.documentElement)
      .getPropertyValue("--primary-color")
      .trim();
    if (cssVar) return cssVar;
  }
  return "#d9534f"; // app default
};

/* Darken a hex color by a factor (0..1) for a subtle gradient end-stop. */
const shade = (hex, factor = 0.78) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return hex;
  const r = Math.round(parseInt(m[1], 16) * factor);
  const g = Math.round(parseInt(m[2], 16) * factor);
  const b = Math.round(parseInt(m[3], 16) * factor);
  return `rgb(${r}, ${g}, ${b})`;
};

/**
 * Reusable Vendor ID Card (redesigned).
 *
 * A styled identity card: gradient accent header, avatar/initial badge,
 * hotel identity, status pill and a clean contact grid, plus a
 * "Download (PNG)" button. Uses html2canvas with a deterministic CORS
 * fallback: if the company logo taints the canvas, the logo node is dropped
 * and the export is retried so the download never hard-fails.
 *
 * Usage:
 *  - Inline:  <VendorIdCard vendor={vendor} />
 *  - Modal:   <VendorIdCard vendor={vendor} asModal open={open} onClose={fn} />
 */
function VendorIdCard({ vendor, asModal = false, open = false, onClose }) {
  const cardRef = useRef(null);
  const [hideLogo, setHideLogo] = useState(false); // deterministic CORS fallback

  const { logo } = LS_SERVICE.get("setting") || {};
  const companyLogo = logo ? IMAGE_BASE_URL + logo : null;

  const capture = () =>
    html2canvas(cardRef.current, { useCORS: true, scale: 2 });

  const downloadCard = async () => {
    if (!cardRef.current || !vendor) return;
    let canvas;
    try {
      canvas = await capture(); // attempt with logo
    } catch (e) {
      // CORS-tainted logo: drop the logo node and retry so export never hard-fails
      setHideLogo(true);
      await new Promise(requestAnimationFrame); // let the logo node unmount
      canvas = await capture();
    }
    const link = document.createElement("a");
    link.download = `ID-${HOTEL_ID_SERIES}${vendor.hotel_id}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
    setHideLogo(false);
  };

  if (!vendor) return null;

  // hotel_id may arrive raw (ViewVendor) or already prefixed (table rows).
  const hotelIdLabel = String(vendor.hotel_id || "").startsWith(HOTEL_ID_SERIES)
    ? vendor.hotel_id
    : HOTEL_ID_SERIES + vendor.hotel_id;

  const initial = (vendor.hotel_name || vendor.vendor_name || "?")
    .trim()
    .charAt(0)
    .toUpperCase();

  const statusColor =
    vendor.status === "active"
      ? "#16a34a"
      : vendor.status === "inactive"
        ? "#dc2626"
        : "#d97706";

  // Tenant primary color (resolved to concrete hex for PNG export).
  const primary = resolvePrimaryHex();
  const primaryDark = shade(primary, 0.72);
  const headerGradient = `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)`;

  const infoRow = (icon, label, value) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "7px 0",
        borderBottom: "1px dashed #eceff3",
      }}
    >
      <span style={{ color: primary, fontSize: 14, width: 16 }}>{icon}</span>
      <span style={{ fontSize: 11, color: "#9aa3af", width: 92 }}>{label}</span>
      <span style={{ fontSize: 13, color: "#1f2937", fontWeight: 500 }}>
        {value || "-"}
      </span>
    </div>
  );

  const card = (
    <>
      <Flex
        justify={asModal ? "flex-end" : "space-between"}
        align="center"
        wrap
        gap="small"
        style={{ marginBottom: 14 }}
      >
        {!asModal && <h3 style={{ margin: 0 }}>Vendor ID Card</h3>}
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={downloadCard}
        >
          Download ID Card (PNG)
        </Button>
      </Flex>

      <div
        ref={cardRef}
        style={{
          width: 380,
          borderRadius: 16,
          overflow: "hidden",
          background: "#ffffff",
          boxShadow: "0 10px 30px rgba(79, 70, 229, 0.12)",
          border: "1px solid #eef0f5",
          fontFamily:
            "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        }}
      >
        {/* Gradient accent header + overlapping avatar */}
        <div
          style={{
            position: "relative",
            height: 110,
            background: headerGradient,
          }}
        >
          {/* decorative circles */}
          <div
            style={{
              position: "absolute",
              top: -24,
              right: -16,
              width: 90,
              height: 90,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.14)",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: 10,
              right: 40,
              width: 50,
              height: 50,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.10)",
            }}
          />
          {!hideLogo && companyLogo && (
            <img
              src={companyLogo}
              alt="Logo"
              style={{
                position: "absolute",
                top: 14,
                right: 16,
                height: 30,
                objectFit: "contain",
                filter: "brightness(0) invert(1)",
                opacity: 0.95,
              }}
            />
          )}
          <span
            style={{
              position: "absolute",
              top: 16,
              left: 22,
              color: "rgba(255,255,255,0.9)",
              fontSize: 11,
              letterSpacing: 1.5,
              fontWeight: 600,
            }}
          >
            VENDOR ID CARD
          </span>

          {/* Avatar overlapping the bottom edge of the header */}
          <div
            style={{
              position: "absolute",
              left: 22,
              bottom: -34,
              width: 68,
              height: 68,
              borderRadius: "50%",
              background: headerGradient,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 28,
              fontWeight: 700,
              border: "4px solid #fff",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            {initial}
          </div>
        </div>

        {/* Identity (leaves room for the overlapping avatar) */}
        <div style={{ padding: "44px 22px 20px" }}>
          <div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: "#111827",
                lineHeight: 1.2,
              }}
            >
              {vendor.hotel_name}
            </div>
            <Flex align="center" gap={8} style={{ marginTop: 6 }}>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: primary,
                  background: "rgba(0,0,0,0.04)",
                  padding: "2px 10px",
                  borderRadius: 20,
                }}
              >
                {hotelIdLabel}
              </span>
              {vendor.status && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#fff",
                    background: statusColor,
                    padding: "2px 10px",
                    borderRadius: 20,
                    textTransform: "capitalize",
                  }}
                >
                  {vendor.status}
                </span>
              )}
            </Flex>
          </div>

          {/* Info grid */}
          <div style={{ marginTop: 16 }}>
            {infoRow(<UserOutlined />, "Vendor", vendor.vendor_name)}
            {infoRow(
              <UserOutlined />,
              "Contact",
              vendor.hotel_contact_person,
            )}
            {infoRow(<PhoneOutlined />, "Mobile", vendor.hotel_mobile_no)}
            {infoRow(<MailOutlined />, "Email", vendor.hotel_email_id)}
            {vendor.hotel_area &&
              infoRow(<EnvironmentOutlined />, "Area", vendor.hotel_area)}
            {vendor.chm_hotel_code &&
              infoRow(<UserOutlined />, "CHM Code", vendor.chm_hotel_code)}
          </div>
        </div>
      </div>
    </>
  );

  if (asModal) {
    return (
      <Modal
        title="Vendor ID Card"
        open={open}
        onCancel={onClose}
        footer={null}
        destroyOnClose
        width={440}
      >
        {card}
      </Modal>
    );
  }

  return card;
}

export default VendorIdCard;
